import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuItem,
  Select,
  Card,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { transactionService } from "../../utils/services/billingPaymentService";
import SuccessModal from "../../components/payment-sucess-modal";
import CustomTypography from "../../globalStyles/fonts";

const ManagePayment = ({ onAdd }) => {
  const [paymentMethod, setPaymentMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);

  const navigate = useNavigate();
  const isFormValid = amount > 0 && currency !== "";

  const resetFieldsAndNavigate = () => {
    setPaymentMethod("");
    setAmount("");
    setCurrency("");
    // navigate("/transaction", { replace: true });
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    resetFieldsAndNavigate();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await transactionService.create({ amount, currency });

      if (response.approvalUrl) {
        window.open(response.approvalUrl, "_blank");
        setTransaction(response);
      } else {
        console.error("Failed to get PayPal approval URL");
      }
    } catch (error) {
      console.error("Error creating PayPal payment:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (transaction) {
      const interval = setInterval(async () => {
        try {
          const response = await transactionService.get(transaction._id);

          if (response.status === "COMPLETED") {
            setLoading(false);
            setShowSuccessModal(true);
            clearInterval(interval);
            setPaymentMethod("");
            setAmount("");
            setCurrency("");
            setTimeout(() => {
              handleCloseSuccessModal();
            }, 5000);
          }
        } catch (error) {
          console.error("Error fetching transaction:", error);
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [transaction]);

  return (
    <Box
      sx={{
        width: "100%",
        margin: "auto",
        mt: 4,
        p: 3,
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <></>
      )}
      <Typography
        fontWeight="500"
        sx={{ mb: 1, textAlign: "start" }}
        fontFamily={"poppins"}
        fontSize={"14px"}
      >
        Select Payment Method
      </Typography>
      <Select
        value={paymentMethod || ""}
        onChange={(e) => setPaymentMethod(e.target.value)}
        fullWidth
        size="small"
        sx={{
          textAlign: "start",
          "& .MuiSelect-select": {
            textAlign: "start",
            color: "#0070FF",
            fontFamily: "poppins",
            fontSize: "14px",
            fontWeight: 500,
          },
        }}
      >
        <MenuItem value="PayPal">PayPal</MenuItem>
        <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
      </Select>

      {paymentMethod === "PayPal" && (
        <Card
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid #D0D5DD",
            boxShadow: 3,
          }}
        >
          <CardContent>
            <CustomTypography style={{ textAlign: "start" }}>
              Amount<span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              type="number"
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Enter Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              margin="dense"
              inputProps={{ min: 1 }}
            />
            <CustomTypography style={{ textAlign: "start", marginTop: 1 }}>
              Currency<span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <Select
              placeholder="Selecet Currency"
              fullWidth
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              size="small"
              sx={{
                mt: 1,
                textAlign: "start",
                "& .MuiSelect-select": {
                  textAlign: "start",
                },
              }}
            >
              <MenuItem value="USD">USD</MenuItem>
            </Select>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={resetFieldsAndNavigate}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!isFormValid}
              >
                Proceed
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {paymentMethod === "Bank Transfer" && (
        <Card
          sx={{
            boxShadow: 3,
            width: "100%",
            height: "100%",
            borderRadius: "6px",
            border: "1px solid #D0D5DD",
            mt: 1,
            p: 2,
          }}
        >
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={5}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"var(--text-tertiary)"}
                >
                  Beneficiary Bank :
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"#606060"}
                >
                  Citibank NA
                </Typography>
              </Grid>

              <Grid item xs={5}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"var(--text-tertiary)"}
                >
                  Account Name :
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"#606060"}
                >
                  BOXC Logistics INC
                </Typography>
              </Grid>

              <Grid item xs={5}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"var(--text-tertiary)"}
                >
                  Account Number :
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"#606060"}
                >
                  123456789
                </Typography>
              </Grid>

              <Grid item xs={5}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"var(--text-tertiary)"}
                >
                  Routing Number :
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"#606060"}
                >
                  9874566
                </Typography>
              </Grid>

              <Grid item xs={5}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"var(--text-tertiary)"}
                >
                  SWIFT Code :
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography
                  c
                  fontWeight="500"
                  sx={{ textAlign: "left" }}
                  fontFamily={"poppins"}
                  fontSize={"14px"}
                  color={"#606060"}
                >
                  CITIUS33
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={resetFieldsAndNavigate}
            >
              Cancel
            </Button>
          </Box>
        </Card>
      )}

      <SuccessModal open={showSuccessModal} onClose={handleCloseSuccessModal} />
    </Box>
  );
};

export default ManagePayment;
